import {parseWorkstreamsPlace, workstreamsPath, workstreamsPlacePath} from './workstreamsRoutes';

// TEMPORARY: once WORKSTREAM_LEGACY_LINK_OPENED stops arriving in Amplitude
// (old bookmarks, docs and pre-swap notifications have aged out), delete this
// file, its test, and the effect that runs it in WorkstreamsPage.

// The views this page has; any other `view` is an old saved view's id.
const VIEWS = new Set(['metrics', 'wireframes', 'roadmap']);

// Old params this page doesn't have (scope and its value, the old filters, comment links).
const DROPPED_PARAMS = ['scope', 'value', 'outcomes', 'narrative_only', 'mode', 'thread'];

// What made a link old, for the WORKSTREAM_LEGACY_LINK_OPENED event. Kinds
// only, never the values (no ldaps, pillar names or ids).
export type LegacyLinkKind =
  | 'scope_company'
  | 'scope_pillar'
  | 'scope_person'
  | 'scope_other'
  | 'old_filter'
  | 'metrics_mode'
  | 'planning_mode'
  | 'saved_view'
  | 'exec_sponsor_inbox'
  | 'activity'
  | 'thread';

/**
 * Where a link to the shipped /workstreams page opens here, and what made it
 * old; null when it's already this page's own. Old links come from bookmarks,
 * Slack and docs, and from notifications sent before the swap:
 *   ?scope=company                → /company
 *   ?scope=pillar&value=X         → /company?pillar=X
 *   ?scope=person&value=ldap      → ?person=ldap (yours is My workstreams)
 *   ?scope=team|direct_reports|following|custom|my_workstreams → My workstreams
 *   ?mode=metrics                 → ?view=metrics (planning mode is gone)
 *   ?view=<saved view id>         → dropped
 *   /exec-sponsor-inbox           → /inbox
 *   /:id/activity, /:id?thread=…  → /:id/detail (Activity lives there)
 * `timeframe` uses the same values, and this page's own params ride along.
 */
export const translateLegacyWorkstreamsLink = ({
  id,
  page,
  search,
}: {
  id?: string;
  page?: string;
  search: string;
}): {href: string; kinds: LegacyLinkKind[]} | null => {
  const params = new URLSearchParams(search);
  const workstreamId = id && /^\d+$/.test(id) ? Number(id) : undefined;
  const scope = params.get('scope');
  const scopeValue = params.get('value')?.trim() || undefined;
  const oldMode = params.get('mode');
  const view = params.get('view');
  const place = parseWorkstreamsPlace(id);

  const kinds: LegacyLinkKind[] = [];
  if (scope !== null) {
    kinds.push(
      scope === 'company'
        ? 'scope_company'
        : scope === 'pillar'
          ? 'scope_pillar'
          : scope === 'person'
            ? 'scope_person'
            : 'scope_other'
    );
  }
  if (params.has('outcomes') || params.has('narrative_only')) kinds.push('old_filter');
  if (oldMode !== null) kinds.push(oldMode === 'metrics' ? 'metrics_mode' : 'planning_mode');
  if (view !== null && !VIEWS.has(view)) kinds.push('saved_view');
  if (id === 'exec-sponsor-inbox') kinds.push('exec_sponsor_inbox');
  if (workstreamId !== undefined && page === 'activity') kinds.push('activity');
  if (params.has('thread')) kinds.push('thread');
  if (kinds.length === 0) return null;

  for (const key of DROPPED_PARAMS) params.delete(key);
  if (kinds.includes('saved_view')) params.delete('view');
  if (oldMode === 'metrics' && !params.has('view')) params.set('view', 'metrics');

  let path: string;
  if (kinds.includes('exec_sponsor_inbox')) {
    path = workstreamsPlacePath('inbox');
  } else if (workstreamId !== undefined) {
    // Activity and comments live on the full page.
    const toDetail = page === 'detail' || kinds.includes('activity') || kinds.includes('thread');
    path = workstreamsPath(workstreamId, toDetail ? 'detail' : 'focus');
  } else if (place && scope === null) {
    path = workstreamsPlacePath(place);
  } else if (scope === 'company') {
    path = workstreamsPlacePath('company');
  } else if (scope === 'pillar' && scopeValue) {
    path = workstreamsPlacePath('company');
    params.set('pillar', scopeValue);
  } else {
    path = workstreamsPath();
    if (scope === 'person' && scopeValue) params.set('person', scopeValue);
  }
  const query = params.toString();
  return {href: `${path}${query ? `?${query}` : ''}`, kinds};
};
